/* eslint-disable react/prop-types */
/* eslint-disable react-refresh/only-export-components */
import {
  fetchSessionCard,
  fetchSessionList,
  getContentById,
  notificationList,
  readById,
  saveContentById,
  sendInstantAudio,
  uploadRawFileApi,
  exportPdfApi,
} from "@/app/api";
import { CONTENT_TEST, USAGE_CARD } from "@/modules/home/constant";
import { generateUUID } from "@/modules/home/utils/homeUtils";
import { handleResponseError } from "@/utils/errorHandler";
import { createContext, useContext, useRef, useState, useCallback, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "@/utils/useToast";


const AudioContext = createContext();

export const AudioProvider = ({ children }) => {
  const [mediaStream, setMediaStream] = useState(null);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [audioChunks, setAudioChunks] = useState([]);
  const mediaRecorderRef = useRef(null);
  const [language, setLanguage] = useState("");
  const [lowVolumeAlert, setLowVolumeAlert] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const timerRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [passingData, setPassingData] = useState(false);
  const [sessionId, setSessionId] = useState("");
  const [realTimeTranslation, setRealTimeTranslation] = useState(false);
  const [content, setContent] = useState(CONTENT_TEST);
  const [usageCard, setUsageCard] = useState(USAGE_CARD);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const navigate = useNavigate();

  // Add debugger to key state setters
  const debugSetContent = (val) => {
    debugger;
    setContent(val);
  };
  const debugSetSessionId = (val) => {
    debugger;
    setSessionId(val);
  };
  const debugSetLoading = (val) => {
    debugger;
    setLoading(val);
  };

  // Memoize all context functions
  const uploadRawFile = useCallback(async (file, options = {}) => {
    setLoading(true);
    try {
      const sessionId = options.sessionId || generateUUID();
      const timestamp = Date.now().toString();
      const fileType = file.type;
      const originalFileName = file.name;
      const fileExtension = originalFileName.split('.').pop();
      const newFile = new File([file], `${originalFileName}-${timestamp}`, { type: fileType });
      const formData = new FormData();
      formData.append("file", newFile);
      formData.append("timeStamp", timestamp);
      formData.append("sessionId", sessionId);
      formData.append("type", "file");
      if (options.chunkIndex !== undefined) {
        formData.append("chunkIndex", options.chunkIndex);
        formData.append("totalChunks", options.totalChunks);
        formData.append("originalFileName", originalFileName);
      }
      const response = await uploadRawFileApi(formData);
      if (!options.chunkIndex || options.chunkIndex === options.totalChunks - 1) {
        setIsModalOpen(false);
        toast.success("File uploaded successfully");
      }
      setLoading(false);
      return response;
    } catch (error) {
      console.error("Upload error:", error);
      await handleResponseError(error);
      setLoading(false);
      return null;
    }
  }, [setLoading, setIsModalOpen]);

  const handleContent = useCallback(({ content, sessionId }) => {
    setContent(content);
    navigate(`/session/${sessionId}/edit`);
  }, [navigate]);

  const getMarkDownContent = useCallback(async (data) => {
    setLoading(true);
    setContent("");
    try {
      if (data) {
        const res = await getContentById(data);
        if (res.data.status === "ON_DEMAND") {
          setContent(res.data.data);
        }
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      await handleResponseError(error);
    }
  }, [handleContent]);

  const saveContent = useCallback(async (sessionId) => {
    setLoading(true);
    try {
      if (sessionId) {
        await saveContentById({ sessionId, content });
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      await handleResponseError(error);
    }
  }, [content]);

  const uploadAudioChunk = useCallback(async (chunks) => {
    try {
      if (!sessionId) {
        console.error("No session ID found, cannot upload audio chunk.");
        return;
      }
      const timestamp = Date.now().toString();
      const blob = new Blob(chunks, { type: "audio/webm" });
      const formData = new FormData();
      const file = new File([blob], `audio-${timestamp}.webm`, {
        type: "audio/webm",
      });
      formData.append("file", file);
      formData.append("timeStamp", timestamp);
      formData.append("sessionId", sessionId);
      const response = await sendInstantAudio(formData);
      console.log("Audio chunk uploaded successfully:", response.data);
    } catch (error) {
      console.error("Error uploading audio chunk:", error);
    }
  }, [sessionId]);

  const startMediaRecorder = useCallback(async () => {
    try {
      if (isRecording) return;
      if (!sessionId) {
        console.error("Session not initialized!");
        return;
      }
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setMediaStream(stream);
      const recorder = new MediaRecorder(stream, { mimeType: "audio/webm" });
      setMediaRecorder(recorder);
      mediaRecorderRef.current = recorder;
      setIsRecording(true);
      setElapsedTime(0);
      startTimer();
      let chunks = [];
      setPassingData(true);
      recorder.ondataavailable = async (event) => {
        chunks.push(event.data);
        const totalSize = chunks.reduce((acc, chunk) => acc + chunk.size, 0);
        console.log({ totalSize });
        if (totalSize >= 1048576) {
          await uploadAudioChunk(chunks);
          chunks = [];
        }
      };
      recorder.onstop = async () => {
        if (chunks.length > 0) {
          await uploadAudioChunk(chunks);
        }
        setAudioChunks(chunks);
        setIsRecording(false);
      };
      recorder.start(1000);
    } catch (error) {
      console.error("Error starting media recorder:", error);
    }
  }, [isRecording, sessionId, uploadAudioChunk]);

  const startTimer = useCallback(() => {
    timerRef.current = setInterval(() => {
      setElapsedTime((prev) => prev + 1);
    }, 1000);
  }, []);

  const stopTimer = useCallback(() => {
    clearInterval(timerRef.current);
    timerRef.current = null;
  }, []);

  const stopMediaRecorder = useCallback(() => {
    setPassingData(false);
    if (mediaRecorder && mediaRecorder.state === "recording") {
      mediaRecorder.stop();
      if (mediaStream) {
        mediaStream.getTracks().forEach((track) => track.stop());
        setMediaStream(null);
      }
      stopTimer();
      setIsRecording(false);
      return true;
    }
    return false;
  }, [mediaRecorder, mediaStream, stopTimer]);

  const getUsageCard = useCallback(async (sessionId) => {
    console.log({ sessionId });
    setLoading(true);
    try {
      if (sessionId) {
        const res = await fetchSessionCard(sessionId);
        setUsageCard(res.data);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      await handleResponseError(error);
    }
  }, []);

  const getUsageTable = useCallback(async (sessionId) => {
    setLoading(true);
    try {
      if (sessionId) {
        await fetchSessionList(sessionId);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      await handleResponseError(error);
    }
  }, []);

  const getNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const res = await notificationList();
      setNotifications(res.data);
      setLoading(false);
    } catch (error) {
      await handleResponseError(error);
    }
  }, []);

  const readNotificationById = useCallback(async (id) => {
    setLoading(true);
    try {
      await readById(id);
      setNotifications((prevNotifications) =>
        prevNotifications.map((notification) =>
          notification._id === id
            ? { ...notification, read: true }
            : notification
        )
      );
      setLoading(false);
    } catch (error) {
      await handleResponseError(error);
    }
  }, []);

  const handleEdit = useCallback(async (sessionId) => {
    if (!sessionId) return;
    setLoading(true);
    setContent("");
    const toastId = toast.loading("Fetching your content");
    try {
      const res = await getContentById(sessionId);
      if (res.data.status === "ON_DEMAND") {
        setContent(res.data.data);
        navigate(`/session/${sessionId}/edit`);
      } else if (res.data.status === "QUEUE") {
        setLoading(true);
        console.log("QUEUE 1");
      }
    } catch (error) {
      await handleResponseError(error);
    } finally {
      toast.dismiss(toastId);
    }
  }, [navigate, handleContent]);

  const exportAsPDF = useCallback(async (sessionId, fileName) => {
    try {
      const response = await exportPdfApi(sessionId, fileName);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${fileName}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("PDF downloaded successfully!");
    } catch (error) {
      console.error("PDF export error:", error);
      toast.error("Failed to download PDF");
    }
  }, []);

  // Memoize context value
  const value = useMemo(() => ({
    handleEdit,
    readNotificationById,
    getNotifications,
    uploadRawFile,
    setContent,
    content,
    realTimeTranslation,
    setRealTimeTranslation,
    setSessionId,
    passingData,
    loading,
    elapsedTime,
    isRecording,
    mediaStream,
    mediaRecorder,
    audioChunks,
    startMediaRecorder,
    stopMediaRecorder,
    lowVolumeAlert,
    setLowVolumeAlert,
    language,
    setLanguage,
    getMarkDownContent,
    saveContent,
    getUsageCard,
    getUsageTable,
    usageCard,
    setUsageCard,
    isModalOpen,
    setIsModalOpen,
    notifications,
    setNotifications,
    exportAsPDF,
  }), [
    handleEdit,
    readNotificationById,
    getNotifications,
    uploadRawFile,
    setContent,
    content,
    realTimeTranslation,
    setRealTimeTranslation,
    setSessionId,
    passingData,
    loading,
    elapsedTime,
    isRecording,
    mediaStream,
    mediaRecorder,
    audioChunks,
    startMediaRecorder,
    stopMediaRecorder,
    lowVolumeAlert,
    setLowVolumeAlert,
    language,
    setLanguage,
    getMarkDownContent,
    saveContent,
    getUsageCard,
    getUsageTable,
    usageCard,
    setUsageCard,
    isModalOpen,
    setIsModalOpen,
    notifications,
    setNotifications,
    exportAsPDF,
  ]);

  return (
    <AudioContext.Provider value={value}>
      {children}
    </AudioContext.Provider>
  );
};

// Custom Hook to Use the Audio Context
export const useAudio = () => {
  const context = useContext(AudioContext);
  if (!context) {
    throw new Error("useAudio must be used within an AudioProvider");
  }
  return context;
};
